import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create as modelsCreate } from 'pkg/api/models/actions';
import type { EventFlags, RSVPInterval } from 'pkg/api/models/event';
import * as sdk from 'pkg/core/sdk';
import * as routes from 'pkg/router/routes';
import { pushState, replaceState } from 'pkg/router/state';
import { getTimeZoneByGmtString } from 'pkg/timezone';

import type { IEditSeriesFormState } from 'routes/event/interfaces';

import { Event, trackEvent, trackedGroup } from 'components/analytics';

interface CreateEventSeriesUser {
	userId: number;
	role?: string;
}

interface CreateEventSeriesPayload {
	groupId: number;
	timezone?: string;
	title: string;
	description?: string;
	location?: string;
	type?: string;
	duration: number;
	rRuleString: string;
	isPrivate: boolean;
	isPublic: boolean;
	hideParticipants: boolean;
	meetBeforeMinutes?: number;
	capacity?: number | null;
	rsvpInterval?: RSVPInterval;
	rsvpIntervalCount?: number;
	inviteInterval?: RSVPInterval;
	inviteIntervalCount?: number;
	reminderInterval?: RSVPInterval;
	reminderIntervalCount?: number;
	users?: CreateEventSeriesUser[];
	sendInvites: boolean;
	flags: EventFlags[];
	inviteUsersToEvent?: boolean;
	inviteAdminAndStaffToEvent?: boolean;
	inviteUsersAsPending?: boolean;
}

export const create = async (
	organizationId: number,
	data: IEditSeriesFormState,
	rRuleString: string,
	attachments: models.attachment.Attachment[] = []
): Promise<models.eventSeries.EventSeries> => {
	const eventData: CreateEventSeriesPayload = {
		groupId: data.groupId,
		title: data.title,
		description: data.description,
		location: data.location,
		type: data.type,
		duration: data.endsAt - data.startsAt,
		rRuleString,
		timezone: getTimeZoneByGmtString(data.timezone).name,
		isPrivate: data.isPrivate,
		isPublic: data.isPublic,
		hideParticipants: data.hideParticipants,
		capacity: data.capacity,
		sendInvites: data.sendInvites,
		users: data.selectedUsers.map((u) => ({
			userId: u.id,
			role: data.organizer[u.id] ? 'organizer' : 'participant',
		})),
		flags: data.flags,
	};

	if (data.inviteUsers || data.inviteUsersAsPending) {
		eventData.inviteUsersToEvent = true;
		eventData.inviteAdminAndStaffToEvent = true;
		eventData.inviteUsersAsPending = !!data.inviteUsersAsPending;
	}

	if (data.meetBeforeMinutes) {
		eventData.meetBeforeMinutes = Number.parseInt(data.meetBeforeMinutes, 10);
	}

	if (data.rsvpBefore) {
		eventData.rsvpInterval = data.rsvpInterval;
		eventData.rsvpIntervalCount = data.rsvpIntervalCount;
	}

	if (data.inviteBefore) {
		eventData.inviteInterval = data.inviteInterval;
		eventData.inviteIntervalCount = data.inviteIntervalCount;
	}

	if (data.reminderBefore) {
		eventData.reminderInterval = data.reminderInterval;
		eventData.reminderIntervalCount = data.reminderIntervalCount;
	}

	const [request, response] = await modelsCreate<
		CreateEventSeriesPayload,
		models.eventSeries.EventSeries
	>(endpoints.EventSeries.Create(organizationId), eventData);

	if (!request.ok) {
		flashActions.show({
			title: t('Something went wrong'),
			message: t(
				"If you think this error isn't supposed to happen, please contact 360Player support."
			),
		});

		return;
	}

	trackEvent(Event.EventSeriesCreated, {
		...trackedGroup(eventData.groupId),
		event_type: data.type,
		sends_invites: !!data.sendInvites,
	});

	attachments.forEach((a) =>
		sdk.post(
			endpoints.EventSeries.AddAttachment(organizationId, response.id, a.id)
		)
	);

	return response;
};

export const remove = async (
	organizationId: number,
	id: number,
	delete_all: boolean = false
) => {
	const request = await sdk.destroy(
		endpoints.EventSeries.Delete(organizationId, id),
		{},
		{ delete_all }
	);
	const response = await request.json();

	if (response.message !== 'ok') {
		return;
	}

	flashActions.show({
		title: !delete_all
			? t(`Successfully removed series.`)
			: t(`Successfully removed series and connected events.`),
	});

	replaceState(routes.Organization.Home(organizationId));
};

interface UpdateEventSeriesPayload {
	timezone: string;
	title: string;
	description: string;
	location: string;
	duration: number;
	rRuleString: string;
	isPrivate: boolean;
	isPublic: boolean;
	hideParticipants: boolean;
	meetBeforeMinutes: number;
	capacity?: number | null;
	rsvpInterval?: RSVPInterval | '';
	rsvpIntervalCount?: number;
	inviteInterval?: RSVPInterval | '';
	inviteIntervalCount?: number;
	reminderInterval?: RSVPInterval | '';
	reminderIntervalCount?: number;
	users?: CreateEventSeriesUser[];
	sendInvites: boolean;
	flags: EventFlags[];
	inviteUsersToEvent?: boolean;
	inviteAdminAndStaffToEvent?: boolean;
	inviteUsersAsPending?: boolean;
}

export const update = async (
	organizationId: number,
	eventSeries: models.eventSeries.EventSeries,
	data: IEditSeriesFormState,
	rRuleString: string,
	eventSeriesUsers: models.eventSeriesUser.EventSeriesUser[],
	attachments: models.attachment.Attachment[] = []
): Promise<boolean> => {
	const eventData: UpdateEventSeriesPayload = {
		timezone: data.timezone ? getTimeZoneByGmtString(data.timezone).name : '',
		title: data.title,
		location: data.location,
		description: data.description,
		duration: data.endsAt - data.startsAt,
		rRuleString,
		isPrivate: data.isPrivate,
		isPublic: data.isPublic,
		hideParticipants: data.hideParticipants,
		meetBeforeMinutes: 0,
		capacity: data.capacity,
		sendInvites: data.sendInvites,
		users: data.selectedUsers.map((u) => ({
			userId: u.id,
			role: data.organizer[u.id] ? 'organizer' : 'participant',
		})),
		flags: data.flags,
	};

	if (data.inviteUsers || data.inviteUsersAsPending) {
		eventData.inviteUsersToEvent = true;
		eventData.inviteAdminAndStaffToEvent = true;
		eventData.inviteUsersAsPending = !!data.inviteUsersAsPending;
	}

	if (data.meetBeforeMinutes) {
		eventData.meetBeforeMinutes = Number.parseInt(data.meetBeforeMinutes, 10);
	}

	if (data.rsvpBefore) {
		eventData.rsvpInterval = data.rsvpInterval;
		eventData.rsvpIntervalCount = data.rsvpIntervalCount;
	} else {
		eventData.rsvpInterval = '';
		eventData.rsvpIntervalCount = 0;
	}

	if (data.inviteBefore) {
		eventData.inviteInterval = data.inviteInterval;
		eventData.inviteIntervalCount = data.inviteIntervalCount;
	} else {
		eventData.inviteInterval = '';
		eventData.inviteIntervalCount = 0;
	}

	if (data.reminderBefore) {
		eventData.reminderInterval = data.reminderInterval;
		eventData.reminderIntervalCount = data.reminderIntervalCount;
	} else {
		eventData.reminderInterval = '';
		eventData.reminderIntervalCount = 0;
	}

	const request = await sdk.patch(
		endpoints.EventSeries.Update(organizationId, eventSeries.id),
		{},
		eventData
	);
	if (!request.ok) {
		const response = await request.json().catch(() => ({}));

		flashActions.show(
			{
				title: t('Could not update event series'),
				message: response?.error,
			},
			request.status
		);

		return false;
	}

	await Promise.all(
		attachments.map((a) =>
			sdk.post(
				endpoints.EventSeries.AddAttachment(
					organizationId,
					eventSeries.id,
					a.id
				)
			)
		)
	);

	pushState(routes.EventSeries.View(organizationId, eventSeries.id));

	return true;
};

export async function deleteSeriesAttachment(
	organizationId: number,
	eventSeriesId: number,
	attachmentId: number
) {
	const request = await sdk.destroy(
		endpoints.EventSeries.RemoveAttachment(
			organizationId,
			eventSeriesId,
			attachmentId
		)
	);

	if (request.ok) {
		return true;
	}

	flashActions.show({
		title: t('Something went wrong'),
	});

	return false;
}
